import { useMemo, useState } from "react";
import type { AssignmentSegment, NormalizedRoundSchedule } from "../../domain/round/types";
import { filterManualBoardPeople, type ManualBoardPersonOption } from "./manual-board-model";

const SEGMENT_LABEL: Partial<Record<AssignmentSegment, string>> = {
  full18: "18 dołków",
  holes1to9: "1–9",
  holes10to18: "10–18",
};

export type ManualBoardAddTarget = {
  groupId: string;
  groupNumber: number;
  segment: AssignmentSegment;
};

export function ManualBoardPersonPicker({
  target,
  people,
  schedule,
  onPick,
  onClose,
}: {
  target: ManualBoardAddTarget;
  people: ManualBoardPersonOption[];
  schedule: NormalizedRoundSchedule;
  onPick: (personId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => filterManualBoardPeople(people, query), [people, query]);
  const summaryById = useMemo(() => new Map(schedule.people.map((person) => [person.personId, person])), [schedule.people]);

  return (
    <div className="mbp-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="mbp-dialog" role="dialog" aria-modal="true" aria-label="Dodaj scorera" onMouseDown={(event) => event.stopPropagation()}>
        <header className="mbp-header">
          <div>
            <span className="eyebrow">DODAJ SCORERA</span>
            <h3>G{target.groupNumber} · {SEGMENT_LABEL[target.segment] || target.segment}</h3>
            <p>Możesz wybrać również osobę, która już pracuje w innej grupie. To pozwala ręcznie dołożyć kolejne dołki.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Zamknij">×</button>
        </header>

        <label className="mbp-search">
          <span>Szukaj osoby lub noclegu</span>
          <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="np. Kowal albo Hotel" />
        </label>

        <div className="mbp-list">
          {filtered.map((person) => {
            const summary = summaryById.get(person.personId);
            const assignmentCount = schedule.assignments.filter((assignment) => assignment.personId === person.personId).length;
            return (
              <button type="button" className="mbp-person" key={person.personId} onClick={() => onPick(person.personId)}>
                <div>
                  <strong>{person.displayName}</strong>
                  <span>{person.baseName}</span>
                </div>
                <div className="mbp-person-meta">
                  {person.driverEligible ? <small className="driver">K</small> : null}
                  <small>{summary?.holes || 0} dołków</small>
                  <small>{assignmentCount} poz.</small>
                </div>
              </button>
            );
          })}
          {!filtered.length ? <div className="mbp-empty">Brak osób pasujących do wyszukiwania.</div> : null}
        </div>
      </section>
    </div>
  );
}
